import * as OktaAuth from '@okta/okta-auth-js/dist/okta-auth-js.min.js';
import * as Cookies from 'js-cookie';
import { CookieAttributes } from 'js-cookie';

export interface FramedAuthConfig {
    url: string;
    clientId: string;
    redirectUri: string;
    scopes: string[];
    scheme: string;
    ssoDomain: string;
    ssoCookie: string;
}

export class FramedAuthManager {
    private static TOKEN_RENEWAL_THRESHOLD = 0.25; // Renew tokens after 15% of TTL remains

    public readonly authClient = null;
    private readonly cookieAttributes: CookieAttributes;

    constructor(public readonly config: FramedAuthConfig) {
        this.authClient = new OktaAuth({
            url: this.config.url,
            clientId: this.config.clientId,
            redirectUri: this.config.redirectUri
        });
        this.cookieAttributes = {
            path: '/',
            domain: config.ssoDomain,
            secure: config.scheme === 'https'
        };
    }

    private refreshToken(oldToken) {
        this.authClient.token
            .renew({
                idToken: oldToken,
                scopes: this.config.scopes
            })
            .then(response => {
                if (response && response.claims && response.idToken) {
                    this.postClaimsMessage(response.idToken, response.claims);
                } else {
                    this.postClaimsMessage(null, null);
                }
            })
            .catch(err => {
                console.error(err);
                this.postClaimsMessage(null, null);
            });
    }

    private isAllowedUrl(url: string) {
        if (!url || url.length === 0) {
            return false;
        }

        const domainRegStr = this.config.ssoDomain.replace(/\./g, '\\.');
        const schemeStr = this.config.scheme;
        const urlSecurityReg = new RegExp(`${schemeStr}://[\\w\\.-]*${domainRegStr}([?\/#:]|$)`);
        return urlSecurityReg.test(url);
    }

    private postClaimsMessage(newToken, newClaims) {
        if (!newToken || !newClaims) {
            // Claims/token could not be renewed, remove cookie to force re-authorization
            Cookies.remove(this.config.ssoCookie, this.cookieAttributes);
        } else if (Cookies.get(this.config.ssoCookie) !== newToken) {
            // If a new token is provided then updated cookie
            Cookies.set(this.config.ssoCookie, newToken, this.cookieAttributes);
        }

        // Update framed parent (if any)
        if (document.referrer && window.parent && this.isAllowedUrl(document.referrer)) {
            window.parent.postMessage(
                {
                    jwt: newToken,
                    claims: newClaims
                },
                document.referrer
            );
        }
    }

    isPastRenewalThreshold(claims) {
        if (!claims || !claims.payload || !claims.payload.exp || !claims.payload.iat) {
            // Always try renewal if unable to verify token expiry
            return true;
        }

        // Return true if time remain before token expiry (exp) surpasses the minimum renewal
        // threshold ratio of the total token lifetime (exp - iat (issue time))
        return (
            claims.payload.exp - new Date().getTime() / 1000 <=
            (claims.payload.exp - claims.payload.iat) * FramedAuthManager.TOKEN_RENEWAL_THRESHOLD
        );
    }

    refreshSSOCookie() {
        const token = Cookies.get(this.config.ssoCookie);
        if (token && token.length && token.length > 64) {
            try {
                const claims = this.authClient.token.decode(token);
                if (this.isPastRenewalThreshold(claims)) {
                    // Existing token needs renewal - start renew then send
                    this.refreshToken(token);
                } else {
                    // Existing token is valid - send it
                    this.postClaimsMessage(token, claims.payload);
                }
            } catch (err) {
                // Invalid / corrupted token -- send invalid auth data
                this.postClaimsMessage(null, null);
            }
        } else {
            // No token available -- send invalid auth data
            this.postClaimsMessage(null, null);
        }
    }
}
window['auth'] = {
    FramedAuthManager: FramedAuthManager
};
